import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, X } from 'lucide-react';
import moment from 'moment';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { userService } from "../../_services";
import CanvasCardFront from "../Summary/Cards/CanvasCardFront";
import Faq from "./SummaryFaq";
import AppleModal from "./AppleModal";
import WisePayment from "./WisePayment";
import AppleWallet from "../../images/apple-wallet.svg";
import GoogleWallet from "../../images/google-wallet.svg";
import OpenUrl from "../../images/open_url_summary.svg";
import { DollarSign } from "lucide-react";
import paymentBg from "../../images/payment-back.png";
import Resources from "../../resources.json";
import { cn } from "../../lib/utils";

/**
 * Consumer Summary Page - Exact dev-v3 layout with Shadcn
 * All 6 summary sections with proper data display
 */
function SummaryPage() {
    const [userData, setUserData] = useState([]);
    const [shortname, setShortname] = useState('');
    const [fullName, setFullName] = useState('');
    const [primaryGn, setPrimaryGn] = useState('');
    const [primaryGroupData, setPrimaryGroupData] = useState(null);
    const [cashbackRewards, setCashbackRewards] = useState([]);
    const [paidTransactionBox, setPaidTransactionBox] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [noTransaction, setNoTransaction] = useState(false);
    const [noCashback, setNoCashback] = useState(false);
    const [emailList, setEmailList] = useState([]);
    const [emailField, setEmailField] = useState('');
    const [errors, setErrors] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [loader, setLoader] = useState(false);
    const [paidLoader, setPaidLoader] = useState(false);
    const [deviceName, setDeviceName] = useState('');
    const [browserName, setBrowserName] = useState('');
    const [applePopup, setApplePopup] = useState(false);
    const [showApplePopup, setShowApplePopup] = useState(false);
    const [showPaymentPopup, setShowPaymentPopup] = useState(false);
    const [accountSummary, setAccountSummary] = useState('');
    const [longAccountSummary, setLongAccountSummary] = useState('');
    const [summaryLoading, setSummaryLoading] = useState(true);

    useEffect(() => {
        try {
            const userAgent = window.navigator.userAgent;
            const devicetype = getUserAgent(userAgent);
            const browsername = getBrowserName(userAgent);
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');

            setTimeout(() => {
                getUserSummaryDetails(userId, userName);
            }, 100);

            setTimeout(() => {
                getPaidTransactionBox(userId, selectedMonth);
            }, 100);

            getConsumerGroups(userId, userName);

            setDeviceName(devicetype);
            setBrowserName(browsername);
        } catch (error) {
            console.error('Error initializing:', error);
        }
    }, []);

    const getUserAgent = (userAgent) => {
        if (/Mobi|Android/i.test(userAgent)) {
            if (/iPhone|iPad|iPod/i.test(userAgent)) {
                return 'iPhone';
            } else {
                return 'Android';
            }
        } else {
            return 'Desktop';
        }
    };

    const getBrowserName = (userAgent) => {
        if (/Firefox/i.test(userAgent)) {
            return 'Firefox';
        } else if (/Chrome/i.test(userAgent)) {
            return 'Chrome';
        } else if (/Safari/i.test(userAgent)) {
            return 'Safari';
        } else if (/MSIE|Trident/i.test(userAgent)) {
            return 'Internet Explorer';
        } else if (/Edge/i.test(userAgent)) {
            return 'Edge';
        } else if (/Opera|OPR/i.test(userAgent)) {
            return 'Opera';
        } else if (/Brave/i.test(userAgent)) {
            return 'Brave';
        } else if (/Vivaldi/i.test(userAgent)) {
            return 'Vivaldi';
        } else if (/Yandex/i.test(userAgent)) {
            return 'Yandex';
        } else if (/Chromium/i.test(userAgent)) {
            return 'Chromium';
        } else {
            return 'Unknown';
        }
    };

    const getUserSummaryDetails = async (userId, userName) => {
        setSummaryLoading(true);
        try {
            const apidata = await userService.Summary_Details(userId, userName);

            if (apidata && apidata.length > 0) {
                localStorage.setItem('hacker_user_detail', window.btoa(apidata[0].shortName + ':' + apidata[0].fullName));
                const usernamedata = window.atob(localStorage.getItem('hacker_user_detail'));

                const userAgent = window.navigator.userAgent;
                if (!(/Safari/.test(userAgent) && /Version/.test(userAgent) && !/CriOS/.test(userAgent)) && deviceName === 'iPhone') {
                    setShowApplePopup(true);
                }

                setCashbackRewards(apidata[0].cashback || []);
                setAccountSummary(apidata[0].accountSummary || '');
                setLongAccountSummary(apidata[0].longAccountSummary || '');
                setUserData(apidata);
                setShortname(usernamedata.split(':')[0]);
                setFullName(usernamedata.split(':')[1]);

                if (apidata[0].cashback && apidata[0].cashback.length < 3) {
                    setNoCashback(true);
                }

                // Pass generation is deferred to getConsumerGroups where group number is available
            }
        } catch (error) {
            console.error('Error getting summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const getConsumerGroups = async (userId, userName) => {
        try {
            const apidata = await userService.GetConsumerGroups(userId, userName);
            if (apidata && apidata.length > 0) {
                // Use primary group if available, otherwise fallback to first
                const primaryGroup = apidata.find(g => g.isPrimary) || apidata[0];
                const groupNo = primaryGroup.groupnumber || '';
                setPrimaryGn(groupNo);
                setPrimaryGroupData(primaryGroup);
                // Generate wallet passes now that we have the group number
                appleGooglePassClick('apple', groupNo);
                appleGooglePassClick('google', groupNo);
            }
        } catch (error) {
            console.error('Error getting consumer groups:', error);
        }
    };

    const getPaidTransactionBox = async (userId, monthYear) => {
        setPaidLoader(true);
        const MonthYear = moment(new Date(monthYear)).format('MMM yyyy');

        try {
            const apidata = await userService.Paid_transaction_box(userId, MonthYear);

            if (apidata && apidata.length > 0) {
                setNoTransaction(apidata.length < 3);
                setPaidTransactionBox(apidata);
            }
            setPaidLoader(false);
        } catch (error) {
            console.error('Error getting transactions:', error);
            setPaidLoader(false);
        }
    };

    const handleMonthChange = (e) => {
        setSelectedMonth(e);
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        getPaidTransactionBox(Usermeta_Data.split(':')[0], e);
    };

    const shareCard = async () => {
        setLoader(true);
        setErrorEmail('');

        try {
            let emails = [...emailList];
            const fValue = emailField.trim();
            let isCheck = true;

            if (errorEmail.length < 1) {
                if (fValue === '' && emails.length < 1) {
                    setErrorEmail('Email required');
                    setLoader(false);
                    isCheck = false;
                } else if (fValue !== '') {
                    if (userService.ValidEmailRegex(fValue) === false) {
                        setEmailField('');
                        setErrorEmail('Invalid email');
                        setLoader(false);
                        isCheck = false;
                    } else {
                        if (!emails.includes(fValue.toLowerCase())) {
                            emails.push(fValue);
                        }
                    }
                }

                if (emails.length <= 10) {
                    if (emails.length > 0 && isCheck) {
                        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                        const userId = Usermeta_Data.split(':')[0];
                        const userName = Usermeta_Data.split(':')[1];
                        const JsonEmails = JSON.stringify(emails);

                        try {
                            const apidata = await userService.Share_Card(userId, userName, JsonEmails);

                            if (apidata[0].code === "1") {
                                setErrors('Your card is successfully shared');
                                setEmailField('');
                                setEmailList([]);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            } else {
                                setErrors(apidata[0].message);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            }
                        } catch (error) {
                            console.error('Error sharing card:', error);
                            setLoader(false);
                        }
                    }
                } else {
                    setEmailField('');
                    setErrorEmail('Max allowed email id is up to 10');
                    isCheck = false;
                    setLoader(false);
                }
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Error in shareCard:', error);
            setLoader(false);
        }
    };

    const handleEmailKeyUp = (e) => {
        try {
            setErrors('');
            setErrorEmail('');
            const email = e.target.value;

            if (email.indexOf(',') > -1) {
                e.target.value = '';
                const inputemail = email.replace(',', '').toLowerCase();

                if (userService.ValidEmailRegex(inputemail) === true) {
                    let existingitem = [...emailList];

                    const isDuplicate = existingitem.some(item => item === inputemail);

                    if (isDuplicate === false) {
                        if (existingitem.length < 10) {
                            existingitem.push(inputemail);
                            setEmailList(existingitem);
                            setEmailField('');
                        } else {
                            setErrorEmail('Max allowed email id is up to 10');
                            setEmailField('');
                            setLoader(false);
                        }
                    } else {
                        setErrorEmail('Email already exits');
                        setEmailField('');
                        setLoader(false);
                    }
                } else {
                    setErrorEmail('Invalid email');
                    setEmailField('');
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error('Error in handleEmailKeyUp:', error);
        }
    };

    const removeEmail = (index) => {
        try {
            setErrors('');
            const updatedList = [...emailList];
            updatedList.splice(index, 1);
            setEmailList(updatedList);
        } catch (error) {
            console.error('Error removing email:', error);
        }
    };

    const appleGooglePassClick = async (passtype, groupnum) => {
        try {
            const bin = Resources.ApplicationResources.cards[0].bin;
            const pcn = Resources.ApplicationResources.cards[0].pcn;
            const memberid = Resources.ApplicationResources.cards[0].memberid;
            const appname = Resources.ApplicationResources.pass[0].appname;
            // URL differentiates apple vs google; API passtype field must be 'generic' (matches dev-v3)
            const requestURL = passtype === 'apple'
                ? Resources.ApplicationResources.pass[0].applelink
                : Resources.ApplicationResources.pass[0].googlelink;

            // Always regenerate if group number is available to avoid stale cached links
            const apidata = await userService.apple_google_wallet_pass_link(requestURL, bin, pcn, groupnum, memberid, 'generic', appname);

            if (apidata && apidata !== 'null') {
                if (passtype === 'apple') {
                    window.localStorage.setItem('applePass', apidata);
                } else {
                    window.localStorage.setItem('googlePass', apidata);
                }
            }
        } catch (error) {
            console.error('Error generating pass:', error);
        }
    };

    const togglePaymentPopup = () => {
        setShowPaymentPopup(!showPaymentPopup);
    };

    // Called when WisePayment form saves successfully � refresh summary so
    // accountSummary gets populated and the "Get Paid" button is hidden.
    const handlePaymentSuccess = async () => {
        setShowPaymentPopup(false);
        setSummaryLoading(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            const apidata = await userService.Summary_Details(userId, userName);
            if (apidata && apidata.length > 0) {
                setAccountSummary(apidata[0].accountSummary || '');
                setLongAccountSummary(apidata[0].longAccountSummary || '');
            }
        } catch (error) {
            console.error('Error refreshing summary after payment setup:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const appleWalletPopup = () => {
        setApplePopup(!applePopup);
    };

    const openAppleLink = () => {
        const link = window.localStorage.getItem("applePass");
        if (link && link !== 'null') window.open(link, '_blank');
    };

    const openGoogleLink = () => {
        const link = window.localStorage.getItem("googlePass");
        if (link && link !== 'null') window.open(link, '_blank');
    };

    const handleEmailFocus = () => {
        setErrors('');
        setErrorEmail('');
    };

    // Check if user is logged in
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    localStorage.setItem('pagename', 'consumerSummaryPage');

    const showAppleWallet = deviceName !== 'Android';
    const showGoogleWallet = deviceName !== 'iPhone';

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Summary</h1>
            </div>

            {/* Summary Grid - 6 Sections in 3x2 Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 1. Cashback Rewards */}
                <Card>
                    <CardContent className="p-4 sm:p-6 flex flex-col h-full">
                        <h3 className="text-lg font-semibold mb-4">Cashback Rewards</h3>
                        
                        <div className="space-y-4 flex-grow">
                            {/* Current Balance */}
                            <div className="pb-4">
                                <span className="text-sm text-muted-foreground block mb-1">Current Balance</span>
                                {noCashback || cashbackRewards.length <= 0 ? (
                                    <span className="text-4xl font-bold text-accent">$0</span>
                                ) : (
                                    <span className="text-4xl font-bold text-accent">
                                        ${cashbackRewards[0]?.balance?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".") || '0'}
                                    </span>
                                )}
                            </div>

                            {/* Transaction List */}
                            {noCashback ? (
                                <div className="text-muted-foreground text-center py-4 border-t">
                                    no transactions yet
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {cashbackRewards.map((item, index) => {
                                        if (item.amountType !== 'Aggregate_Balance' && item.amountType !== 'Current_Balance') {
                                            return (
                                                <li key={index} className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 mt-1">
                                                        <DollarSign className="w-5 h-5 text-accent" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-medium">{item.date_Month}</span>
                                                            <span className="text-sm font-semibold text-accent">
                                                                ${item.balance?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".") || '0'}
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-muted-foreground">{item.status}</span>
                                                    </div>
                                                </li>
                                            );
                                        }
                                        return null;
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* Earned Till Date - Light Orange Strip at Bottom */}
                        {noCashback || cashbackRewards.length <= 0 ? (
                            <div className="mt-auto -mx-4 -mb-4 px-4 sm:-mx-6 sm:-mb-6 sm:px-6 py-3 rounded-b-lg text-sm font-medium bg-accent-light">
                                Earned Till Date: $0
                            </div>
                        ) : (
                            <div className="mt-auto -mx-4 -mb-4 px-4 sm:-mx-6 sm:-mb-6 sm:px-6 py-3 rounded-b-lg text-sm font-medium bg-accent-light">
                                Earned Till Date: ${cashbackRewards[1]?.balance?.toFixed(2) || '0'}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* 2. Paid Transaction Box */}
                <Card>
                    <CardContent className="p-4 sm:p-6 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Paid Transaction Box</h3>
                            <MonthYearPicker
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                minDate={new Date("2017-01-01")}
                                maxDate={new Date()}
                                className="w-40"
                            />
                        </div>

                        <div className="space-y-4 flex-grow">
                            {/* Transactions for Period */}
                            <div>
                                <span className="text-sm text-muted-foreground block mb-1">Transactions for the Period</span>
                                {noTransaction || paidTransactionBox.length <= 0 ? (
                                    <span className="text-4xl font-bold text-accent">0</span>
                                ) : (
                                    <span className="text-4xl font-bold text-accent">
                                        {paidTransactionBox[0]?.paidClaims || 0}
                                    </span>
                                )}
                            </div>

                            {/* Transaction List */}
                            {noTransaction ? (
                                <div className="text-muted-foreground text-center py-4 border-t">
                                    no transactions yet
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {paidTransactionBox.map((item, index) => {
                                        if (item.pharmacy_Name !== 'AggregateAll' && item.pharmacy_Name !== 'DateWiseAll') {
                                            return (
                                                <li key={index} className="flex items-start gap-3">
                                                    <div className="flex-shrink-0">
                                                        <img src={item.ph_Logo} alt={item.pharmacy_Name} className="w-10 h-10 object-contain" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium">{item.pharmacy_Name}</div>
                                                        <div className="text-xs text-muted-foreground">{item.ph_Address}</div>
                                                        <div className="text-xs text-muted-foreground">{item.paidClaims} transaction(s)</div>
                                                    </div>
                                                </li>
                                            );
                                        }
                                        return null;
                                    })}
                                </ul>
                            )}
                        </div>

                        {/* Total Transactions - Light Orange Strip at Bottom */}
                        {noTransaction || paidTransactionBox.length <= 0 ? (
                            <div className="mt-auto -mx-4 -mb-4 px-4 sm:-mx-6 sm:-mb-6 sm:px-6 py-3 rounded-b-lg text-sm font-medium bg-accent-light">
                                Total transactions: 0
                            </div>
                        ) : (
                            <div className="mt-auto -mx-4 -mb-4 px-4 sm:-mx-6 sm:-mb-6 sm:px-6 py-3 rounded-b-lg text-sm font-medium bg-accent-light">
                                Total transactions: {paidTransactionBox[1]?.paidClaims || 0}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* 3. Payments Section */}
                <Card
                    style={{
                        background: `#fff url(${paymentBg}) right bottom no-repeat`,
                        backgroundSize: '120px'
                    }}>
                    <CardContent 
                        className="p-4 sm:p-6 rounded-lg"
                    >
                        <h3 className="text-lg font-semibold mb-2">Payments</h3>
                        <p className="text-muted-foreground mb-4">Making payments is easier now!!</p>
                        <div className="flex gap-3">
                            <Button variant="secondary" asChild>
                                <a href="/Consumer/faq/#cashback" target="_self">
                                    How it works
                                </a>
                            </Button>
                            {!summaryLoading && userData.length > 0 && (accountSummary === null || accountSummary === '') && (
                                <Button variant="outline" onClick={togglePaymentPopup}>
                                    Get Paid
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* 4. Share Card */}
                <Card>
                    <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold">Share card</h3>
                            <Link to="/consumer/card">
                                <img src={OpenUrl} alt="Open" className="w-4 h-4" />
                            </Link>
                        </div>
                        <p className="text-muted-foreground mb-4">
                            Enter up to 10 Email ID's separated by a comma.
                        </p>

                        <div className="space-y-4">
                            {/* Input and Button Row */}
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="flex-1 relative">
                                    <Input
                                        id="shareEmail"
                                        type="text"
                                        value={emailField}
                                        onChange={(e) => setEmailField(e.target.value)}
                                        onKeyUp={handleEmailKeyUp}
                                        onFocus={handleEmailFocus}
                                        className={cn("peer h-10 mb-0", errorEmail && "border-destructive")}
                                        placeholder=" "
                                    />
                                    <Label 
                                        htmlFor="shareEmail" 
                                        className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none"
                                    >
                                        Enter your e-mail address
                                    </Label>
                                    {errorEmail && (
                                        <p className="text-xs text-destructive mt-1">{errorEmail}</p>
                                    )}
                                </div>

                                <Button
                                    onClick={shareCard}
                                    disabled={loader}
                                    variant="secondary"
                                    className="h-10 min-w-24 w-full sm:w-auto"
                                >
                                    {loader ? (
                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sharing...</>
                                    ) : (
                                        'Share'
                                    )}
                                </Button>
                            </div>

                            {emailList.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {emailList.map((email, index) => (
                                        <Badge key={index} variant="secondary" className="gap-1 pr-1">
                                            {email}
                                            <button
                                                onClick={() => removeEmail(index)}
                                                className="ml-1 hover:bg-black/20 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {errors && (
                                <Alert variant={errors.includes('successfully') ? 'success' : 'destructive'}>
                                    <AlertDescription>{errors}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* 5. Get Your Cards */}
                <Card>
                    <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-lg font-semibold">Get your cards</h3>
                            <Link to="/consumer/card">
                                <img src={OpenUrl} alt="Open" className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="space-y-4">
                            {/* Card Preview - Using modernized Shadcn card */}
                            <div className="flex justify-center">
                                <CanvasCardFront
                                    isCvs={false}
                                    bin="023518"
                                    pcn="ARX"
                                    groupNumber={primaryGn}
                                    memberId="100-4542"
                                />
                            </div>

                            {/* Wallet Buttons */}
                            <div className="flex gap-3 justify-center">
                                {showAppleWallet && (
                                    <button
                                        onClick={showApplePopup ? appleWalletPopup : openAppleLink}
                                        className="transition-transform hover:scale-105"
                                    >
                                        <img src={AppleWallet} alt="Apple Wallet" className="h-10" />
                                    </button>
                                )}
                                {showGoogleWallet && (
                                    <button
                                        onClick={openGoogleLink}
                                        className="transition-transform hover:scale-105"
                                    >
                                        <img src={GoogleWallet} alt="Google Wallet" className="h-10" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 6. FAQ Section */}
                <Card>
                    <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-lg font-semibold">FAQ</h3>
                            <Link to="/Consumer/faq">
                                <img src={OpenUrl} alt="Open" className="w-4 h-4" />
                            </Link>
                        </div>
                        <Faq summaryfaq="8" />
                    </CardContent>
                </Card>
            </div>

            {/* Modals */}
            {applePopup && (
                <AppleModal closeModal={appleWalletPopup} />
            )}

            {showPaymentPopup && (
                <WisePayment
                    closePaymentPopup={togglePaymentPopup}
                    onSuccess={handlePaymentSuccess}
                />
            )}
        </div>
    );
}

export default SummaryPage;

